"use client";
import {
  AlertTriangle,
  Bot,
  Copy,
  Download,
  File,
  KeyRound,
  LoaderCircle,
  Play,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  getCompanionHealth,
  runLocalDynamicAnalysis,
  saveCompanionToken,
  type CompanionHealth,
  type DynamicSandboxResult,
} from "../../../lib/local-companion";
import {
  type ScanReport,
} from "../../../lib/types";
import {
  formatDuration,
  truncateMiddle,
} from "../../shared/format";


export function ExternalAnalysisView({
  report,
  file,
  onCopy,
}: {
  report: ScanReport;
  file: File | null;
  onCopy: (value: string, message?: string) => void;
}) {
  const [pairingCode, setPairingCode] = useState("");
  const [companionHealth, setCompanionHealth] =
    useState<CompanionHealth | null>(null);
  const [localResult, setLocalResult] =
    useState<DynamicSandboxResult | null>(null);
  const [localRunning, setLocalRunning] = useState(false);
  const [localError, setLocalError] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [privacy, setPrivacy] = useState<"owner" | "public">("owner");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [taskId, setTaskId] = useState("");
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState("");

  const refreshCompanion = useCallback(async () => {
    const health = await getCompanionHealth();
    setCompanionHealth(health);
    return health;
  }, []);

  useEffect(() => {
    let active = true;
    void getCompanionHealth().then((health) => {
      if (active) setCompanionHealth(health);
    });
    return () => {
      active = false;
    };
  }, []);

  const pairCompanion = async () => {
    saveCompanionToken(pairingCode);
    const health = await refreshCompanion();
    if (!health.paired) {
      setLocalError("Pairing failed. Copy the current code from the companion.");
    } else {
      setLocalError("");
      setPairingCode("");
    }
  };

  const runLocal = async () => {
    if (!file || localRunning) return;
    setLocalRunning(true);
    setLocalError("");
    setLocalResult(null);
    try {
      setLocalResult(await runLocalDynamicAnalysis(file));
    } catch (runError) {
      setLocalError(
        runError instanceof Error
          ? runError.message
          : "The local sandbox failed.",
      );
    } finally {
      setLocalRunning(false);
      void refreshCompanion();
    }
  };

  const submit = async () => {
    if (!file || !apiKey.trim() || !consent || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const body = new FormData();
      body.set("file", file, file.name);
      body.set("privacy", privacy);
      const response = await fetch("/api/integrations/anyrun/submit", {
        method: "POST",
        headers: { "x-anyrun-api-key": apiKey.trim() },
        body,
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(
          payload?.message ?? payload?.error ?? "ANY.RUN rejected the submission.",
        );
      }
      const id =
        payload?.data?.taskid ??
        payload?.data?.uuid ??
        payload?.taskid ??
        payload?.uuid ??
        "";
      setTaskId(String(id));
      setResult(payload);
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : "The sandbox submission failed.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const refresh = async () => {
    if (!taskId || !apiKey.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(
        `/api/integrations/anyrun/report?task=${encodeURIComponent(taskId)}`,
        { headers: { "x-anyrun-api-key": apiKey.trim() } },
      );
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(
          payload?.message ?? payload?.error ?? "The report is not available.",
        );
      }
      setResult(payload);
    } catch (refreshError) {
      setError(
        refreshError instanceof Error
          ? refreshError.message
          : "The sandbox report could not be refreshed.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="view-stack">
      <section className="panel provider-panel local-sandbox-panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">NO THIRD-PARTY API KEY</div>
            <h3>Local disposable sandbox</h3>
          </div>
          <span>
            {companionHealth?.paired
              ? companionHealth.imageReady
                ? "Ready"
                : "Container image required"
              : companionHealth?.available
                ? "Pair once"
                : "Companion offline"}
          </span>
        </div>
        <div className="provider-warning local">
          <ShieldCheck size={18} />
          <p>
            The file is sent only to a service on this computer. Execution uses
            a disposable Docker container with networking disabled, a read-only
            root, dropped Linux capabilities, and strict CPU, memory, process,
            and time limits.
          </p>
        </div>
        <div className="provider-form">
          {!companionHealth?.paired && (
            <label>
              <span>Companion pairing code</span>
              <div>
                <KeyRound size={14} />
                <input
                  value={pairingCode}
                  autoComplete="off"
                  placeholder="Code printed by scripts/jarlab_companion.py"
                  onChange={(event) => setPairingCode(event.target.value)}
                />
              </div>
            </label>
          )}
          {!companionHealth?.paired ? (
            <button
              className="secondary-button"
              type="button"
              disabled={!pairingCode.trim()}
              onClick={() => void pairCompanion()}
            >
              <ShieldCheck size={15} />
              Pair local companion
            </button>
          ) : (
            <button
              className="primary-button dynamic-run-button"
              type="button"
              disabled={!file || !companionHealth.imageReady || localRunning}
              onClick={() => void runLocal()}
            >
              {localRunning ? (
                <LoaderCircle size={16} />
              ) : (
                <Play size={16} />
              )}
              {localRunning ? "Running in disposable container…" : "Run dynamic"}
            </button>
          )}
          {!companionHealth?.available && (
            <div className="companion-setup">
              <small>
                The public browser cannot execute a JAR by itself. Download and
                start the local companion once, then pair using its printed code.
              </small>
              <a href="/JarLab-local-companion.zip" download>
                <Download size={14} />
                Download local companion
              </a>
            </div>
          )}
          {companionHealth?.paired && !companionHealth.imageReady && (
            <small>
              Run the companion with <code>--prepare</code> once to download
              its sandbox image.
            </small>
          )}
          {localError && <div className="inline-error">{localError}</div>}
          {localResult && (
            <div className="dynamic-result">
              <div>
                <span>Exit</span>
                <strong>
                  {localResult.timedOut
                    ? "Timed out"
                    : (localResult.exitCode ?? "unknown")}
                </strong>
              </div>
              <div>
                <span>Duration</span>
                <strong>{formatDuration(localResult.durationMs)}</strong>
              </div>
              <div>
                <span>Filesystem changes</span>
                <strong>{localResult.filesystemChanges.length}</strong>
              </div>
              <details>
                <summary>Sandbox evidence</summary>
                <pre>
                  {JSON.stringify(
                    {
                      ...localResult,
                      stdout: localResult.stdout.slice(0, 120_000),
                      stderr: localResult.stderr.slice(0, 120_000),
                    },
                    null,
                    2,
                  )}
                </pre>
              </details>
            </div>
          )}
        </div>
      </section>

      <section className="panel provider-panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">OPT-IN DYNAMIC ANALYSIS</div>
            <h3>ANY.RUN cloud sandbox</h3>
          </div>
          <span>Bring your own API key</span>
        </div>
        <div className="provider-warning">
          <AlertTriangle size={18} />
          <p>
            Submitting sends the complete sample to ANY.RUN. JARs may need the
            correct Java, Minecraft, or mod-loader environment before runtime
            behavior appears. Your API key is held only in this view and is
            forwarded request-by-request.
          </p>
        </div>
        <div className="provider-form">
          <label>
            <span>ANY.RUN API key</span>
            <div>
              <KeyRound size={14} />
              <input
                type="password"
                value={apiKey}
                autoComplete="off"
                placeholder="Session-only API key"
                onChange={(event) => setApiKey(event.target.value)}
              />
            </div>
          </label>
          <label>
            <span>Submission privacy</span>
            <select
              value={privacy}
              onChange={(event) =>
                setPrivacy(event.target.value as "owner" | "public")
              }
            >
              <option value="owner">Owner only</option>
              <option value="public">Public sample</option>
            </select>
          </label>
          <label className="provider-consent">
            <input
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
            />
            I understand that the full file will leave my browser and be
            processed by ANY.RUN.
          </label>
          <button
            className="primary-button"
            type="button"
            disabled={!file || !apiKey.trim() || !consent || submitting}
            onClick={() => void submit()}
          >
            {submitting ? <LoaderCircle size={15} /> : <Play size={15} />}
            {submitting ? "Working…" : "Submit current file"}
          </button>
          {!file && (
            <small>
              The current session was restored without its original bytes. Run
              a new scan to enable submission.
            </small>
          )}
          {taskId && (
            <div className="provider-task">
              <span>
                Task <code>{taskId}</code>
              </span>
              <button type="button" onClick={() => void refresh()}>
                <RefreshCw size={13} />
                Refresh report
              </button>
            </div>
          )}
          {error && <div className="inline-error">{error}</div>}
          {result !== null && (
            <details className="provider-result">
              <summary>Raw sandbox response</summary>
              <pre>{JSON.stringify(result, null, 2)}</pre>
            </details>
          )}
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">OTHER PROVIDERS</div>
            <h3>External second opinions</h3>
          </div>
          <span>No undocumented upload APIs</span>
        </div>
        <div className="provider-card-grid">
          {[
            {
              name: "IsThisARat",
              url: "https://isthisarat.com/",
              detail:
                "Open its official scanner and submit manually. JarLab cannot embed an API the provider does not publish.",
            },
            {
              name: "RatterScanner",
              url: "https://ratterscanner.com/",
              detail:
                "Open the official service for a separate verdict. The file is not uploaded by JarLab.",
            },
          ].map((provider) => (
            <article key={provider.name}>
              <Bot size={20} />
              <div>
                <strong>{provider.name}</strong>
                <span>{provider.detail}</span>
                <code>{truncateMiddle(report.hashes.sha256, 18, 14)}</code>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => onCopy(report.hashes.sha256, "SHA-256 copied")}
                >
                  <Copy size={13} />
                  Copy hash
                </button>
                <a href={provider.url} target="_blank" rel="noreferrer">
                  Open provider
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
